import { Loader2, Monitor, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { useRelaunchMission } from '../../hooks/useRelaunchMission';
import { compactAge } from '../../lib/bureau/age';
import { doingLine, wakeText, type OttoNowData } from '../../lib/bureau/ottoNow';
import { newMissionHref } from '../../lib/bureau/newMission';
import { openWorkshopScreen } from '../../lib/workshop/screenWindow';
import { LiveMark } from '../workqueue/LiveMark';
import { KeptLink } from './shell/KeptLink';
import { MENU_ITEM, MENU_LABEL, MENU_RULE } from './encre';

const HEADING = MENU_LABEL;
const SPIN = 'h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-[1.5px] border-desk-ring border-t-desk-soft motion-reduce:animate-none';

/** A line that opens its mission in the Bureau, or stays a plain line without one. */
function MissionLink({ threadId, onNavigate, className, children }: { threadId: string | null; onNavigate: () => void; className: string; children: ReactNode }) {
  if (!threadId) return <div className={className}>{children}</div>;
  return (
    <KeptLink to={`/admin/travail?mission=${encodeURIComponent(threadId)}`} onClick={onNavigate} className={twMerge(className, 'hover:bg-desk-hover')}>
      {children}
    </KeptLink>
  );
}

/**
 * The detail of the small Otto (mock-up « Le petit Otto : ce qu'il fait en ce moment »): « En ce
 * moment » — each thing he does, about whom, for how long, opening its mission —, « A calé » with
 * « Relancer » when a mission stopped on a snag, « Ensuite » — his next wake-ups. Its foot
 * (`OttoNowFooter`) holds the ways to talk to him and to read everything he did.
 */
export function OttoNowPanel({ data, timeZone, onNavigate }: { data: OttoNowData; timeZone: string; onNavigate: () => void }) {
  const { relaunch, pendingThreadId, error } = useRelaunchMission();
  const now = new Date();
  const doing = data.doing.map(doingLine);
  return (
    <div>
      <p className={HEADING}>En ce moment</p>
      {doing.length === 0 && <p className="px-2.5 pb-2 text-sm text-desk-muted">Rien en cours : Otto se repose jusqu'à son prochain réveil.</p>}
      {doing.map((line) => (
        <MissionLink key={line.key} threadId={line.threadId} onNavigate={onNavigate} className="flex items-start gap-2.5 rounded-[10px] px-2.5 py-2">
          <span className={twMerge(SPIN, 'mt-[3px]')} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-desk-ink">{line.verb}</span>
            {(line.about || line.detail) && <span className="block truncate text-xs text-desk-muted">{[line.about, line.detail].filter(Boolean).join(' — ')}</span>}
          </span>
          <span className="shrink-0 text-xs text-desk-muted">{compactAge(line.since)}</span>
        </MissionLink>
      ))}

      {data.halted.length > 0 && (
        <>
          <hr className={MENU_RULE} />
          <p className={HEADING}>A calé</p>
          {data.halted.map((h) => (
            <div key={h.thread_id} className="flex items-center gap-2 rounded-[10px] py-1 pl-1 pr-1.5">
              <MissionLink threadId={h.thread_id} onNavigate={onNavigate} className="min-w-0 flex-1 truncate rounded-[10px] px-1.5 py-1.5 text-sm text-desk-ink">
                {h.title ?? 'Une mission'}
              </MissionLink>
              <button
                type="button"
                onClick={() => void relaunch(h.thread_id)}
                disabled={pendingThreadId !== null}
                className="inline-flex h-[26px] shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-medium text-desk-ink ring-1 ring-inset ring-desk-ring hover:bg-desk-hover disabled:opacity-40"
              >
                {pendingThreadId === h.thread_id ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                Relancer
              </button>
            </div>
          ))}
          {error && <p className="px-2.5 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </>
      )}

      {data.next.length > 0 && (
        <>
          <hr className={MENU_RULE} />
          <p className={HEADING}>Ensuite</p>
          {data.next.map((n, i) => {
            const line = doingLine({ kind: 'mission', action: n.action, thread_id: n.thread_id, title: n.title, client: n.client, since: null }, i);
            return (
              <MissionLink
                key={`${n.thread_id}-${n.at}`}
                threadId={n.thread_id}
                onNavigate={onNavigate}
                className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate text-desk-ink">
                  {line.verb}
                  {line.about && <span className="text-desk-soft"> · {line.about}</span>}
                </span>
                <span className="shrink-0 text-xs text-desk-muted">{wakeText(n.at, now, timeZone)}</span>
              </MissionLink>
            );
          })}
        </>
      )}

    </div>
  );
}

/**
 * The foot of the small Otto: « Nouvelle mission » (talking to Otto is giving him a mission, in the
 * Bureau: the old chat panel is gone from here, Gilles 12/10/2026), « Voir son écran » (the machine's browser, the header's
 * workshop window), how fresh the Bureau is (« En direct », « À jour · il y a 8 s », amber
 * « Dernière vérification… » — the old cockpit's mark), and « Tout le journal ».
 */
export function OttoNowFooter({ onNavigate, journalHref = '/admin/travail?vue=journal' }: { onNavigate: () => void; journalHref?: string }) {
  return (
    <div className="flex flex-col">
      <hr className={MENU_RULE} />
      <KeptLink to={journalHref} onClick={onNavigate} className={MENU_ITEM}>
        Tout le journal
      </KeptLink>
      <KeptLink to={newMissionHref()} onClick={onNavigate} className={MENU_ITEM}>
        Nouvelle mission
      </KeptLink>
      <button
        type="button"
        onClick={() => {
          openWorkshopScreen();
          onNavigate();
        }}
        className={MENU_ITEM}
      >
        <Monitor size={16} strokeWidth={1.75} className="text-desk-soft" aria-hidden="true" />
        <span className="flex-1">Voir son écran</span>
      </button>
      <div className="px-2.5 pb-1 pt-1.5">
        <LiveMark />
      </div>
    </div>
  );
}
